import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { progressApi } from '@/api/progress';
import { Button } from '@/components/ui/Button';
import { useConfirm } from '@/components/ui/ConfirmProvider';
import { useIsMobile } from '@/hooks/useIsMobile';
import { manualActionErrorText } from '@/lib/lessonManualActions';

/**
 * «Открыть следующий» — курс «оценивает система» и мини-курс
 * (договор `docs/CONTRACTS/lesson-force-complete.md` §3, решение владельца 13.09.2026).
 *
 * Ученик застрял на уроке не по своей вине — сотрудник открывает ему следующий. Текущий урок
 * остаётся непройденным, баллов за него нет, процент курса не растёт.
 *
 * ⚠️ С ПОДТВЕРЖДЕНИЕМ: отмены нет ни у сервера, ни здесь (§9).
 *
 * ⚠️ Показывает её вызывающий и только на уроке, где ученик стоит (`stuckLessonIds`), — на
 * уроке дальше по цепочке отметка ничего видимого не даст.
 *
 * После успеха список перечитывается: пометка «следующий открыт · кто · когда» приходит с
 * сервера, а не рисуется здесь по догадке. Повторное нажатие даёт `already: true` и ту же
 * первую отметку — отдельного успеха не показываем.
 */
export function OpenNextLessonButton({
  studentId,
  lessonId,
  lessonTitle,
}: {
  studentId: string;
  lessonId: string;
  lessonTitle: string;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const ask = useConfirm();
  const isMobile = useIsMobile();
  const [error, setError] = useState('');

  const open = useMutation({
    mutationFn: () => progressApi.openNextLesson(studentId, lessonId),
    onSuccess: () => {
      setError('');
      qc.invalidateQueries({ queryKey: ['staff', 'student-lessons', studentId] });
    },
    onError: (e) => setError(manualActionErrorText(e, t)),
  });

  const onClick = async () => {
    const ok = await ask({
      title: t('studentProgress.openNextConfirmTitle'),
      message: t('studentProgress.openNextConfirm', { title: lessonTitle }),
      confirmLabel: t('studentProgress.openNext'),
    });
    if (ok) open.mutate();
  };

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      {/* На телефоне — 44 px, норма касания (docs/CURATOR-MOBILE-DESIGN.md §1). */}
      <Button size={isMobile ? 'md' : 'sm'} variant="secondary" onClick={onClick} loading={open.isPending}>
        <span className="material-symbols-outlined">lock_open</span>
        {t('studentProgress.openNext')}
      </Button>
      {error && (
        <span role="alert" className="text-xs text-error">
          {error}
        </span>
      )}
    </div>
  );
}
